/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { dimensionStyle } from './themes/ChartStyles.css';
import { TrackResizeContainer } from '#UNSAFE_TrackResizeContainer';
import { ChartWithDimensions } from './ChartWithDimensions';
import {
  ChartCommonProps,
  ChartSeriesData,
  GetNextChartItemType,
  Info,
  Scale,
  getDataPosType,
  SizeHandle
} from '#utils/UNSAFE_visTypes/chart';
import { Dimension, MarkerShapes, VisTextStyle } from '#utils/UNSAFE_visTypes/common';
import { ComponentChildren, Ref, VNode } from 'preact';
import { Property } from 'csstype';
import { LineAreaItem } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import { ScatterItem } from '#UNSAFE_ScatterChart/scatterChart.types';
import { BarItemData } from '#UNSAFE_BarChart/barChart.types';
import { useOverviewContext } from '#hooks/PRIVATE_useOverviewContext';
import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';
import { forwardRef } from 'preact/compat';

export type ChartProps<K, D> = {
  selectedIds: Set<K>;
  getGapRatio: (startIndex: number, endIndex: number) => number;
  getNavUtil: (startIndex: number, endIndex: number) => GetNextChartItemType;
  getDataItemPos: (
    xScale: Scale,
    yScale: Scale,
    y2Scale: Scale,
    axisStepWidth: number,
    averageGroupZ: number,
    gapRatio: number
  ) => getDataPosType;
  findNearest: (
    xScale: Scale,
    yScale: Scale,
    y2Scale: Scale,
    axisStepWidth: number,
    averageGroupZ: number,
    gapRatio: number
  ) => (
    x: number,
    y?: number,
    y2?: number
  ) =>
    | {
        seriesIndex: number;
        groupIndex: number;
        y: number | undefined;
        y2: number | undefined;
        x: number;
      }
    | undefined;
  children: (context: {
    getItemAriaLabel: (
      data: D,
      seriesIndex: number,
      groupIndex: number,
      isSelectionEnabled: boolean,
      isSelected: boolean,
      isDrillable: boolean
    ) => string;
    xStartIndex: number;
    xEndIndex: number;
    xScale: Scale;
    yScale: Scale;
    y2Scale: Scale;
    gapRatio: number;
    activeId?: string;
    focusedItemInfo: Info;
    hoveredItemInfo?: Info;
    axisStepWidth: number;
    averageGroupZ: number;
    isPointInsideMarquee: ((id: K, point: { x: number; y: number }) => boolean) | undefined;
    getTextDimensions?: (text: string, style: Record<string, any>) => Dimension;
    defaultFontSize: string;
    resolvedColors: string[];
    groupsInfo: GroupLevelItem[];
    plotAreaClipPathId: string;
  }) => ComponentChildren;
  getStartAndEndAxesOffset: (groupCount: number) => { startOffset: number; endOffset: number };
  series: ChartSeriesData<K, D>[];
  isRtl: boolean;
  renderGridLinesInFront: boolean;
  isDataXAxis?: boolean;
  getMarkerInfo: (
    seriesIndex: number,
    groupIndex: number
  ) => { color: Property.Color | undefined; type?: MarkerShapes };
  defaultOverviewContentRenderer: () => ComponentChildren;
  isStacked?: boolean;
  getStackGroup?: (series: ChartSeriesData<K, D>) => string | undefined;
  stackLabelStyle?: VisTextStyle;
} & Omit<ChartCommonProps<K, D>, 'highlightedIds' | 'hoverBehavior' | 'selectedIds'>;

type ForwardRefChart = <
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>
>(
  props: ChartProps<K, D> & { ref?: Ref<SizeHandle> }
) => VNode | null;

export const Chart = forwardRef(
  <K extends string | number, D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>>(
    { width, height, ...props }: ChartProps<K, D>,
    ref: Ref<SizeHandle> = null
  ) => {
    // TODO: reevaluate how we handle empty data
    const {
      isOverview,
      overviewRenderer,
      yAxisSize,
      width: overviewWidth,
      height: overviewHeight
    } = useOverviewContext();

    let chartWidth = width;
    let chartHeight = height;

    if (chartWidth === undefined && isOverview && overviewWidth !== undefined) {
      chartWidth = `${overviewWidth}px`;
    }

    if (chartHeight === undefined && isOverview && overviewHeight !== undefined) {
      chartHeight = `${overviewHeight}px`;
    }

    return (
      <TrackResizeContainer width={chartWidth} height={chartHeight} class={dimensionStyle}>
        {(width, height) =>
          width || height ? (
            <ChartWithDimensions
              ref={ref}
              width={width}
              height={height}
              {...props}
              yAxisSize={yAxisSize}
              isOverview={isOverview}
              overviewRenderer={overviewRenderer}
            />
          ) : undefined
        }
      </TrackResizeContainer>
    );
  }
) as ForwardRefChart;
